import React, { useCallback, useMemo, useRef, useState } from 'react';
import DropDownButton from 'devextreme-react/drop-down-button';
import { CheckBox } from 'devextreme-react/check-box';
import { Button } from 'devextreme-react/button';
import Sortable from 'devextreme-react/sortable';
import AntLocalization from '../utils/AntLocalization';

// Danh sách cột lấy lại mỗi lần mở (ẩn/hiện tự động thay đổi theo dữ liệu). Mỗi dòng: tick = đang hiện.
const ColumnChooserPanel = ({ getItemsRef, onToggleRef, onResetRef, onReorderRef, refreshRef }) => {
    const [items, setItems] = useState(() => (getItemsRef.current ? getItemsRef.current() : []));
    const refresh = () => setItems(getItemsRef.current ? getItemsRef.current() : []);
    // DropDownButton giữ nội dung giữa các lần mở: mở lại thì lấy lại danh sách.
    refreshRef.current = refresh;
    const customized = items.some((item) => item.customized);

    return (
        <div className="ant-grid-column-chooser-panel">
            {/* Kéo biểu tượng bên trái để đổi thứ tự cột (Sortable của DevExtreme). */}
            <Sortable
                className="ant-grid-column-chooser-list"
                filter=".ant-grid-column-chooser-item"
                handle=".ant-grid-column-chooser-handle"
                itemOrientation="vertical"
                dragDirection="vertical"
                onReorder={(e) => {
                    if (e.fromIndex === e.toIndex) return;
                    const next = items.slice();
                    const [moved] = next.splice(e.fromIndex, 1);
                    next.splice(e.toIndex, 0, moved);
                    setItems(next);
                    if (onReorderRef.current) onReorderRef.current(next.map((item) => item.field));
                }}
            >
                {items.map((item) => (
                    <div key={item.field} className="ant-grid-column-chooser-item">
                        <i
                            className="dx-icon dx-icon-dragvertical ant-grid-column-chooser-handle"
                            title={AntLocalization.translateText({ vi: 'Kéo để đổi thứ tự', en: 'Drag to reorder', fr: 'Glisser pour classer', es: 'Arrastre para ordenar', id: 'Seret untuk urutkan', ja: 'ドラッグで並べ替え', zh: '拖动调整顺序', ko: '드래그하여 순서 변경', hi: 'क्रम हेतु खींचें', ar: 'اسحب لإعادة الترتيب' })}
                        />
                        <CheckBox
                            value={item.visible}
                            text={item.caption}
                            disabled={item.locked}
                            hint={
                                item.locked
                                    ? AntLocalization.translateText({ vi: 'Cột bắt buộc hiển thị', en: 'Required column', fr: 'Colonne obligatoire', es: 'Columna obligatoria', id: 'Kolom wajib tampil', ja: '必須表示の列', zh: '必显示列', ko: '필수 표시 열', hi: 'अनिवार्य कॉलम', ar: 'عمود إلزامي' })
                                    : undefined
                            }
                            onValueChanged={(e) => {
                                if (!e.event) return;
                                if (onToggleRef.current) onToggleRef.current(item.field, Boolean(e.value));
                                refresh();
                            }}
                        />
                        {item.autoHidden && (
                            <span
                                className="ant-grid-column-chooser-note"
                                title={AntLocalization.translateText({
                                    vi: 'Đang tự ẩn vì trang hiện tại không có dữ liệu',
                                    en: 'Hidden automatically: no data on this page',
                                    fr: 'Masquée automatiquement : aucune donnée sur cette page',
                                    es: 'Oculta automáticamente: sin datos en esta página',
                                    id: 'Disembunyikan otomatis: tidak ada data di halaman ini',
                                    ja: 'このページにデータがないため自動で非表示',
                                    zh: '当前页无数据，已自动隐藏',
                                    ko: '이 페이지에 데이터가 없어 자동으로 숨김',
                                    hi: 'स्वतः छिपा: इस पृष्ठ पर डेटा नहीं',
                                    ar: 'مخفي تلقائيًا: لا توجد بيانات في هذه الصفحة'
                                })}
                            >
                                {AntLocalization.translateText({ vi: 'trống', en: 'empty', fr: 'vide', es: 'vacía', id: 'kosong', ja: '空', zh: '空', ko: '빈 열', hi: 'खाली', ar: 'فارغ' })}
                            </span>
                        )}
                    </div>
                ))}
            </Sortable>
            <div className="ant-grid-column-chooser-actions">
                <Button
                    text={AntLocalization.translateText({ vi: 'Mặc định', en: 'Default', fr: 'Par défaut', id: 'Utama', ja: 'デフォルト', zh: '默认', ko: '기본', hi: 'डिफ़ॉल्ट', ar: 'الافتراضي', es: 'Por defecto' })}
                    stylingMode="text"
                    disabled={!customized}
                    onClick={() => {
                        if (onResetRef.current) onResetRef.current();
                        refresh();
                    }}
                />
            </div>
        </div>
    );
};

// Nút ẩn/hiện cột trên thanh công cụ AntGrid. Lựa chọn lưu trong cấu hình lưới (AntGridSettings).
export default function AntGridColumnChooser(props) {
    const getItemsRef = useRef(props.getItems);
    getItemsRef.current = props.getItems;
    const onToggleRef = useRef(props.onToggle);
    onToggleRef.current = props.onToggle;
    const onResetRef = useRef(props.onReset);
    onResetRef.current = props.onReset;
    const onReorderRef = useRef(props.onReorder);
    onReorderRef.current = props.onReorder;

    // Hàm render cố định: đổi template lúc popup đang mở làm popup đóng mất.
    const refreshRef = useRef(null);
    const renderPanel = useCallback(
        () => (
            <ColumnChooserPanel
                getItemsRef={getItemsRef}
                onToggleRef={onToggleRef}
                onResetRef={onResetRef}
                onReorderRef={onReorderRef}
                refreshRef={refreshRef}
            />
        ),
        []
    );
    const dropDownOptions = useMemo(
        () => ({
            width: 280,
            maxHeight: 460,
            onShowing: () => {
                if (refreshRef.current) refreshRef.current();
            }
        }),
        []
    );

    return (
        <div className="ant-grid-column-chooser">
            <DropDownButton
                icon="columnchooser"
                text=""
                hint={AntLocalization.translateText({ vi: 'Ẩn hiện cột', en: 'Show/hide columns', fr: 'Colonnes visibles', es: 'Columnas visibles', id: 'Tampil/sembunyi kolom', ja: '列の表示/非表示', zh: '显示/隐藏列', ko: '열 표시/숨김', hi: 'कॉलम दिखाएँ/छिपाएँ', ar: 'إظهار/إخفاء الأعمدة' })}
                showArrowIcon={false}
                stylingMode="text"
                focusStateEnabled={false}
                disabled={props.disabled}
                dropDownOptions={dropDownOptions}
                dropDownContentRender={renderPanel}
            />
        </div>
    );
}
